import { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { Row, Col } from 'react-bootstrap';
import { useForm, ValidationError } from '@formspree/react';
import SplitText from '../components/SplitText';
import '../components/navdropdown.css';

const EXPO = [0.16, 1, 0.3, 1];

const C = {
  bg:         '#FAFAFA',
  white:      '#FFFFFF',
  border:     '#E8E4E1',
  borderSoft: '#F0ECEA',
  ink:        '#1A1A1A',
  inkSoft:    '#3A3A3A',
  muted:      '#7A7A7A',
  silver:     '#B0B0B0',
  red:        '#C8151A',
  redDeep:    '#9B1015',
  redBright:  '#E01E24',
  redPale:    '#FFF0F0',
  redFaint:   'rgba(200,21,26,0.06)',
};

const cardStyle = {
  background: C.white,
  borderRadius: '4px',
  border: `1px solid ${C.border}`,
  borderTop: `3px solid ${C.red}`,
  boxShadow: '0 2px 14px rgba(0,0,0,0.05)',
};

const IconBox = ({ children }) => (
  <div style={{
    width: '36px',
    height: '36px',
    borderRadius: '4px',
    background: C.redPale,
    border: `1px solid rgba(200,21,26,0.12)`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  }}>
    {children}
  </div>
);

/* ─── Fade In Wrapper ─── */
const FadeIn = ({ children, direction = 'up', delay = 0, style = {} }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-60px' });
  return (
    <motion.div
      ref={ref}
      style={{ width: '100%', height: '100%', ...style }}
      initial={{
        opacity: 0,
        y: direction === 'up' ? 24 : 0,
        x: direction === 'left' ? -24 : direction === 'right' ? 24 : 0,
      }}
      animate={isInView ? { opacity: 1, y: 0, x: 0 } : {}}
      transition={{ duration: 0.65, delay, ease: EXPO }}
    >
      {children}
    </motion.div>
  );
};

const ContactUsscreen = () => {
  const [state, handleSubmit] = useForm('mgojloyy');

  const inputBase = {
    width: '100%',
    padding: '11px 14px',
    backgroundColor: C.white,
    border: `1px solid ${C.border}`,
    borderRadius: '4px',
    color: C.ink,
    fontSize: '14px',
    outline: 'none',
    fontFamily: "'Inter', sans-serif",
    transition: 'border-color 0.25s ease, box-shadow 0.25s ease',
    boxSizing: 'border-box',
  };

  const labelStyle = {
    display: 'block',
    fontSize: '10px',
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '2px',
    color: C.muted,
    marginBottom: '6px',
    fontFamily: "'Inter', sans-serif",
  };

  const sectionEyebrow = {
    margin: '0 0 4px',
    fontSize: '10px',
    fontWeight: 700,
    letterSpacing: '3px',
    textTransform: 'uppercase',
    color: C.red,
    fontFamily: "'Inter', sans-serif",
  };

  const sectionTitle = {
    fontSize: '1.15rem',
    fontWeight: 900,
    color: C.ink,
    textTransform: 'uppercase',
    letterSpacing: '-0.2px',
    margin: '0 0 4px',
    lineHeight: 1.25,
    fontFamily: "'Inter', sans-serif",
  };

  return (
    <>
      <style>{`
        .contact-page ::placeholder { color: #BDBDBD; font-family: 'Inter', sans-serif; font-size: 14px; }
        .contact-page .contact-input:focus {
          border-color: #C8151A !important;
          box-shadow: 0 0 0 3px rgba(200,21,26,0.08) !important;
        }
        .contact-page .submit-btn {
          padding: 11px 36px;
          font-size: 12px;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 2px;
          font-family: 'Inter', sans-serif;
        }
        @media (max-width: 991px) {
          .contact-main-grid { flex-direction: column !important; }
          .contact-left-col { flex: 1 1 auto !important; }
        }
      `}</style>

      <div
        className="contact-page"
        style={{
          backgroundColor: C.bg,
          minHeight: '100vh',
          fontFamily: "'Inter', sans-serif",
          position: 'relative',
          overflowX: 'hidden',
        }}
      >
        <div style={{
          position: 'absolute',
          top: '-100px',
          right: '-120px',
          width: '420px',
          height: '420px',
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(200,21,26,0.04) 0%, transparent 70%)',
          pointerEvents: 'none',
        }} />

        {/* Hero */}
        <div style={{
          background: C.white,
          padding: '36px 6% 24px',
          textAlign: 'center',
          position: 'relative',
          overflow: 'hidden',
          borderBottom: `1px solid ${C.border}`,
        }}>
          <div style={{
            position: 'absolute',
            inset: 0,
            pointerEvents: 'none',
            backgroundImage: `radial-gradient(${C.border} 1px, transparent 1px)`,
            backgroundSize: '28px 28px',
            opacity: 0.45,
          }} />
          <div style={{ position: 'relative', zIndex: 1 }}>
            <SplitText
              text="Contact Us"
              tag="h1"
              duration={1.2}
              delay={50}
              splitType="chars"
              style={{
                fontSize: 'clamp(2rem, 4vw, 2.75rem)',
                fontWeight: 900,
                color: C.ink,
                letterSpacing: '-1px',
                textTransform: 'uppercase',
                margin: 0,
                lineHeight: 1.1,
                fontFamily: "'Inter', sans-serif",
              }}
            />
            <motion.div
              initial={{ scaleX: 0 }}
              animate={{ scaleX: 1 }}
              transition={{ duration: 0.6, delay: 0.4, ease: EXPO }}
              style={{
                width: '48px',
                height: '3px',
                background: C.red,
                margin: '10px auto 0',
                borderRadius: '2px',
                transformOrigin: 'center',
              }}
            />
            <motion.p
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.45, ease: EXPO }}
              style={{
                fontSize: '15px',
                lineHeight: '1.75',
                color: C.muted,
                maxWidth: '520px',
                margin: '10px auto 0',
                fontFamily: "'Inter', sans-serif",
              }}
            >
              Product enquiries, custom orders, or scheduling a visit — we're here.
            </motion.p>
          </div>
        </div>

        {/* Main content */}
        <div
          className="contact-main-grid"
          style={{
            maxWidth: '1140px',
            width: '100%',
            margin: '0 auto',
            padding: '28px 6% 56px',
            display: 'flex',
            gap: '24px',
            alignItems: 'stretch',
          }}
        >
          {/* Left: location + map */}
          <div className="contact-left-col" style={{ flex: '0 0 40%', minWidth: 0, display: 'flex', flexDirection: 'column' }}>
            <FadeIn direction="left" delay={0.1} style={{ flex: 1 }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', height: '100%' }}>
                <div style={{ ...cardStyle, padding: '26px 28px' }}>
                  <p style={sectionEyebrow}>Our location</p>
                  <h2 style={sectionTitle}>Decal Tech Private Limited</h2>
                  <p style={{ fontSize: '13px', color: C.muted, margin: '0 0 20px', fontFamily: "'Inter', sans-serif" }}>
                    Manufacturing facility
                  </p>

                  <div style={{
                    display: 'flex',
                    gap: '14px',
                    alignItems: 'flex-start',
                    padding: '16px 0',
                    borderTop: `1px solid ${C.borderSoft}`,
                    borderBottom: `1px solid ${C.borderSoft}`,
                  }}>
                    <IconBox>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={C.red} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                        <circle cx="12" cy="10" r="3" />
                      </svg>
                    </IconBox>
                    <div>
                      <p style={{ ...labelStyle, marginBottom: '6px' }}>Address</p>
                      <p style={{ margin: 0, fontSize: '14px', color: C.inkSoft, lineHeight: '1.65', fontFamily: "'Inter', sans-serif" }}>
                        Unit no. G1-A, Rajlaxmi Hi Tech Park<br />
                        Mumbai–Nashik Highway, Sonale<br />
                        Bhiwandi, Thane – 421302, MH
                      </p>
                    </div>
                  </div>

                  <div style={{ display: 'flex', gap: '14px', alignItems: 'flex-start', paddingTop: '16px' }}>
                    <IconBox>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={C.red} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="12" r="10" />
                        <path d="M12 6v6l4 2" />
                      </svg>
                    </IconBox>
                    <div style={{ flex: 1 }}>
                      <p style={{ ...labelStyle, marginBottom: '10px' }}>Working hours</p>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                        <span style={{ fontSize: '14px', color: C.inkSoft, fontFamily: "'Inter', sans-serif" }}>Monday – Saturday</span>
                        <span style={{ fontSize: '14px', fontWeight: 600, color: C.ink, fontFamily: "'Inter', sans-serif" }}>9:00 AM – 6:00 PM</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span style={{ fontSize: '14px', color: C.muted, fontFamily: "'Inter', sans-serif" }}>Sunday</span>
                        <span style={{ fontSize: '14px', color: C.muted, fontFamily: "'Inter', sans-serif" }}>Closed</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div style={{
                  ...cardStyle,
                  borderTop: `1px solid ${C.border}`,
                  padding: 0,
                  overflow: 'hidden',
                  flex: 1,
                  minHeight: '220px',
                  display: 'flex',
                  flexDirection: 'column',
                }}>
                  <iframe
                    title="Decal Tech Location"
                    src="https://maps.google.com/maps?q=Decal+Tech+India+Pvt+Ltd,+Rajlaxmi+Hi+Tech+Park,+Sonale,+Bhiwandi,+Maharashtra+421302&t=m&z=16&output=embed&iwloc=near"
                    width="100%"
                    style={{ display: 'block', border: 'none', flex: 1, minHeight: '180px' }}
                    allowFullScreen=""
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                  />
                  <a
                    href="https://maps.google.com/?q=Decal+Tech+India+Pvt+Ltd,+Rajlaxmi+Hi+Tech+Park,+Sonale,+Bhiwandi,+Maharashtra+421302"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px',
                      padding: '12px 16px',
                      fontSize: '12px',
                      fontWeight: 700,
                      letterSpacing: '1.5px',
                      textTransform: 'uppercase',
                      color: C.red,
                      textDecoration: 'none',
                      borderTop: `1px solid ${C.borderSoft}`,
                      background: C.redFaint,
                      fontFamily: "'Inter', sans-serif",
                      transition: 'background 0.2s ease',
                    }}
                  >
                    Open in Google Maps
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                      <polyline points="15 3 21 3 21 9" />
                      <line x1="10" y1="14" x2="21" y2="3" />
                    </svg>
                  </a>
                </div>
              </div>
            </FadeIn>
          </div>

          {/* Right: form */}
          <div style={{ flex: 1, minWidth: 0 }}>
            <FadeIn direction="right" delay={0.2} style={{ height: '100%' }}>
              <div style={{
                ...cardStyle,
                padding: '28px 32px 32px',
                height: '100%',
                boxSizing: 'border-box',
                display: 'flex',
                flexDirection: 'column',
              }}>
                <p style={sectionEyebrow}>Send a message</p>
                <h3 style={{ ...sectionTitle, marginBottom: '24px' }}>We'll get back to you</h3>

                {state.succeeded ? (
                  <motion.div
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, ease: EXPO }}
                    style={{
                      flex: 1,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      textAlign: 'center',
                      padding: '32px 16px',
                      gap: '12px',
                    }}
                  >
                    <div style={{
                      width: '52px',
                      height: '52px',
                      borderRadius: '50%',
                      background: '#E8F8F0',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '24px',
                      color: '#22A86B',
                    }}>
                      ✓
                    </div>
                    <p style={{ margin: 0, fontSize: '18px', fontWeight: 700, color: C.ink, fontFamily: "'Inter', sans-serif" }}>
                      Message sent successfully
                    </p>
                    <p style={{ margin: 0, fontSize: '14px', color: C.muted, lineHeight: 1.7, maxWidth: '360px', fontFamily: "'Inter', sans-serif" }}>
                      Thank you for reaching out. Our team will get back to you shortly.
                    </p>
                  </motion.div>
                ) : (
                <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '16px', flex: 1 }}>
                  <Row className="g-3">
                    <Col md={6}>
                      <label htmlFor="name" style={labelStyle}>Full Name *</label>
                      <input className="contact-input" id="name" type="text" name="name" placeholder="Your name" required style={inputBase} />
                      <ValidationError prefix="Name" field="name" errors={state.errors} style={{ color: C.red, fontSize: '12px', marginTop: '4px' }} />
                    </Col>
                    <Col md={6}>
                      <label htmlFor="email" style={labelStyle}>Email Address *</label>
                      <input className="contact-input" id="email" type="email" name="email" placeholder="your@email.com" required style={inputBase} />
                      <ValidationError prefix="Email" field="email" errors={state.errors} style={{ color: C.red, fontSize: '12px', marginTop: '4px' }} />
                    </Col>
                  </Row>

                  <Row className="g-3">
                    <Col md={6}>
                      <label htmlFor="phone" style={labelStyle}>Phone Number</label>
                      <input className="contact-input" id="phone" type="tel" name="phone" placeholder="+91 00000 00000" style={inputBase} />
                      <ValidationError prefix="Phone" field="phone" errors={state.errors} style={{ color: C.red, fontSize: '12px', marginTop: '4px' }} />
                    </Col>
                    <Col md={6}>
                      <label htmlFor="subject" style={labelStyle}>Subject *</label>
                      <input className="contact-input" id="subject" type="text" name="subject" placeholder="How can we help?" required style={inputBase} />
                      <ValidationError prefix="Subject" field="subject" errors={state.errors} style={{ color: C.red, fontSize: '12px', marginTop: '4px' }} />
                    </Col>
                  </Row>

                  <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                    <label htmlFor="message" style={labelStyle}>Message *</label>
                    <textarea
                      className="contact-input"
                      id="message"
                      name="message"
                      placeholder="Tell us about your enquiry, product requirements, or any questions…"
                      required
                      style={{ ...inputBase, resize: 'vertical', flex: 1, minHeight: '120px' }}
                    />
                    <ValidationError prefix="Message" field="message" errors={state.errors} style={{ color: C.red, fontSize: '12px', marginTop: '4px' }} />
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', gap: '16px', paddingTop: '4px' }}>
                    <button
                      className="submit-btn site-cta-btn"
                      type="submit"
                      disabled={state.submitting}
                      style={{
                        padding: '11px 36px',
                        fontSize: '12px',
                        fontWeight: 700,
                        textTransform: 'uppercase',
                        letterSpacing: '2px',
                        fontFamily: "'Inter', sans-serif",
                        opacity: state.submitting ? 0.7 : 1,
                        cursor: state.submitting ? 'not-allowed' : 'pointer',
                      }}
                    >
                      {state.submitting ? 'Sending…' : 'Submit Query'}
                    </button>
                  </div>
                </form>
                )}
              </div>
            </FadeIn>
          </div>
        </div>
      </div>
    </>
  );
};

export default ContactUsscreen;
